import { useMemo } from 'react';
import { PhoneNumber } from '@weave/schema-gen-ts/dist/schemas/phone/callroute/v1/callroute_service.pb';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { formatPhoneNumber } from '@frontend/phone-numbers';
import { useAppScopeStore } from '@frontend/scope';
import { theme } from '@frontend/theme';
import { TextLink, Tabs, Heading, Table, Chip } from '@frontend/design-system';
import { trackingId } from '../../../tracking';
import { EmptyStateSection } from '../empty-state-section';
import { sortRouteExtensionNumbers, sortRoutePhoneNumbers } from '../helpers/dialables';
import { ExtendedCallRoute } from '../types';

export enum NumbersPanelTabs {
  PHONE_NUMBERS = 'phone-numbers',
  EXTENSIONS = 'extensions',
}

type CallRouteTabsProps = {
  initialTab?: NumbersPanelTabs;
  callRoute: ExtendedCallRoute;
  hasChildLocations: boolean;
  isLoading?: boolean;
  onEditPhoneNumbers: (callRouteId: string) => void;
  onEditExtensions: (callRouteId: string) => void;
  onRemovePhoneNumber: (phoneNumberId: string) => void;
  onRemoveExtension: (extensionToRemove: number) => void;
};

export const CallRouteTabs = ({
  initialTab = NumbersPanelTabs.PHONE_NUMBERS,
  callRoute,
  hasChildLocations,
  isLoading = false,
  onEditPhoneNumbers,
  onEditExtensions,
  onRemovePhoneNumber,
  onRemoveExtension,
}: CallRouteTabsProps) => {
  const { t } = useTranslation('phone');
  const { getScopeName } = useAppScopeStore();

  const sortedPhoneNumbers = useMemo(() => sortRoutePhoneNumbers(callRoute.phoneNumbers), [callRoute.phoneNumbers]);

  const sortedExtensionNumbers = useMemo(
    () => sortRouteExtensionNumbers(callRoute.extensionNumbers),
    [callRoute.extensionNumbers]
  );

  return (
    <Tabs initialTab={initialTab}>
      <Tabs.Bar fullWidth={false}>
        <Tabs.Tab
          id='phone-numbers'
          trackingId={trackingId({
            context: 'setting',
            feature: 'call-routes',
            details: 'side-panel-phone-numbers-tab',
          })}
        >
          {t('Phone Numbers')}
        </Tabs.Tab>
        <Tabs.Tab
          id='extensions'
          trackingId={trackingId({
            context: 'setting',
            feature: 'call-routes',
            details: 'side-panel-extensions-tab',
          })}
        >
          {t('Extensions')}
        </Tabs.Tab>
      </Tabs.Bar>

      <Tabs.Panel controller='phone-numbers'>
        <>
          <div
            css={{
              display: 'flex',
              justifyContent: 'space-between',
              marginTop: theme.spacing(2),
            }}
          >
            <Heading level={3}>{t('Phone Numbers')}</Heading>
            <TextLink
              onClick={() => onEditPhoneNumbers(callRoute.callRouteId)}
              weight='bold'
              css={{ marginBottom: theme.spacing(2) }}
              trackingId={trackingId({
                context: 'setting',
                feature: 'call-routes',
                details: 'side-panel-edit-phone-numbers-link',
              })}
            >
              {t('Assign Phone Numbers')}
            </TextLink>
          </div>

          {sortedPhoneNumbers.length > 0 ? (
            <Table
              data={sortedPhoneNumbers}
              globalTrackingId={trackingId({
                context: 'setting',
                feature: 'call-routes',
                details: 'side-panel-numbers-table',
              })}
              colConfig={[
                {
                  Header: t('Phone Number'),
                  accessor: (phoneNumber) => phoneNumber.phoneNumber,
                  id: 'phone-number',
                  cellRenderer: (phoneNumber) => formatPhoneNumber(phoneNumber),
                  width: 180,
                },
                {
                  id: 'phoneNumberName',
                  Header: t('Name'),
                  accessor: (item: PhoneNumber) => (item.displayName ? item.displayName : '--'),
                  width: 180,
                },
                {
                  Header: t('Location'),
                  accessor: (phoneNumber) => phoneNumber.locationId,
                  cellRenderer: (locationId) =>
                    locationId ? <Chip.SingleChip>{getScopeName(locationId)}</Chip.SingleChip> : '',
                  id: 'location-name',
                  sortType: 'caseInsensitive',
                  width: 190,
                  omit: !hasChildLocations,
                },
              ]}
              rowActions={{
                actions: [
                  {
                    label: t('Remove phone number'),
                    Icon: () => <Icon name='remove2' />,
                    onClick: (phoneNumber) => onRemovePhoneNumber(phoneNumber.id),
                    disabled: isLoading,
                  },
                ],
              }}
            />
          ) : (
            <EmptyStateSection
              title={t('No Items to Display')}
              subtitle={t('Click “Assign Phone Numbers" to assign a phone number to this Call Route')}
            />
          )}
        </>
      </Tabs.Panel>

      <Tabs.Panel controller='extensions'>
        <>
          <div
            css={{
              display: 'flex',
              justifyContent: 'space-between',
              marginTop: theme.spacing(2),
            }}
          >
            <Heading level={3}>{t('Extensions')}</Heading>
            <TextLink
              onClick={() => onEditExtensions(callRoute.callRouteId)}
              weight='bold'
              trackingId={trackingId({
                context: 'setting',
                feature: 'call-routes',
                details: 'side-panel-edit-extensions-link',
              })}
              css={{ marginBottom: theme.spacing(2) }}
            >
              {t('Assign Route Extensions')}
            </TextLink>
          </div>

          {sortedExtensionNumbers.length > 0 ? (
            <Table
              data={sortedExtensionNumbers.map((extension) => ({ extension }))}
              globalTrackingId={trackingId({
                context: 'setting',
                feature: 'call-routes',
                details: 'side-panel-extensions-table',
              })}
              colConfig={[
                {
                  Header: t('Extension'),
                  accessor: (row) => row.extension,
                  id: 'extension',
                },
              ]}
              rowActions={{
                actions: [
                  {
                    label: t('Remove extension'),
                    Icon: () => <Icon name='remove2' />,
                    onClick: (row) => onRemoveExtension(row.extension),
                    disabled: isLoading,
                  },
                ],
              }}
            />
          ) : (
            <EmptyStateSection
              title={t('No Items to Display')}
              subtitle={t('Click “Assign Route Extensions" to assign an extension to this Call Route')}
            />
          )}
        </>
      </Tabs.Panel>
    </Tabs>
  );
};
